/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import { Component, registerComponent } from 'js/component.js';
import * as LS from '../../../../locator-shared.js';

const componentName = 'locator-detail';
const { atlas } = window;

class LocatorDetail extends Component {

    // ==========
    // COMPONENT SETUP
    // ==========
    selectors = {
        expertiseBadge: `.${componentName}__expertise-badge`,
    };

    // Top-level templates
    templates = {
        partner: this.element.querySelector(`.${componentName}__partner-container`),
        office: this.element.querySelector(`.${componentName}__office-container`),
        product: this.element.querySelector(`.${componentName}__product-container`),
    };

    // Shared elements
    sharedEls = {
        header: this.element.querySelector(`.${componentName}__header-container`),
        // Sections
        headerBackLink: this.element.querySelector(`.${componentName}__header-back-link`),
        sectionContainers: this.element.querySelectorAll(`.${componentName}__section-container`),
        sectionHeaders: this.element.querySelectorAll(`.${componentName}__section-header`),
        // Templates
        facetListTemplate: this.element.querySelector(`#${componentName}__facet-list-template`),
    };

    // Partner elements
    partnerEls = {
        // Info Section
        infoImage: this.templates.partner.querySelector(`.${componentName}__info-image`),
        infoContactButtons: this.templates.partner.querySelector(`.${componentName}__info-contact-buttons`),
        // Overview Section
        portalImage: this.templates.partner.querySelector(`.${componentName}__portal-image`),
        relatedCompanies: this.templates.partner.querySelector(`.${componentName}__related-companies`),
        operatingCompaniesTitle: this.templates.partner.querySelector(`#${componentName}__operating-companies-title`),
        parentCompanyTitle: this.templates.partner.querySelector(`#${componentName}__parent-company-title`),
        relatedCompaniesContainer: this.templates.partner.querySelector(`.${componentName}__related-companies-container`),
        // Expertise/Offerings Section
        expertiseBadgesContainer: this.templates.partner.querySelector(`.${componentName}__expertise-badges-container`),
        expertiseBadgesTemplate: this.templates.partner.querySelector(`#${componentName}__expertise-badges-template`),
        facetListsContainer: this.templates.partner.querySelector(`.${componentName}__facet-lists-container`),
        productTableBody: this.templates.partner.querySelector(`#${componentName}__product-table-body`),
        productRowTemplate: this.templates.partner.querySelector(`#${componentName}__product-row-template`),
        productPagination: this.templates.partner.querySelector(`#${componentName}__product-pagination`),
        // Regional Contacts Section
        contactsTableBody: this.templates.partner.querySelector(`#${componentName}__contacts-table-body`),
        contactRowTemplate: this.templates.partner.querySelector(`#${componentName}__contact-row-template`),
        // Locations Section
        locationsListContainer: this.templates.partner.querySelector(`.${componentName}__locations-list-container`),
        locationsHeadquartersContainer: this.templates.partner.querySelector(`.${componentName}__locations-hq-container`),
        locationsOtherContainer: this.templates.partner.querySelector(`.${componentName}__locations-other-container`),
        locationTemplate: this.templates.partner.querySelector(`#${componentName}__location-template`),
        locationsMap: this.templates.partner.querySelector(`#${componentName}__locations-map`),
        // Regions Section (for Technology Partners)
        regionsContentContainer: this.templates.partner.querySelector(`.${componentName}__regions-content-container`),
    };

    // Sales office elements
    officeEls = {
        locationCard: this.templates.office.querySelector(`#${componentName}__office-location-card`),
        emailButton: this.templates.office.querySelector(`#${componentName}__office-email-button`),
        phoneButton: this.templates.office.querySelector(`#${componentName}__office-phone-button`),
        map: this.templates.office.querySelector(`#${componentName}__office-map`),
        contactDialog: this.templates.office.querySelector(`#${componentName}__office-contact-dialog`),
        // Sales Office Partner Locations Section
        locationRowTemplate: this.templates.office.querySelector(`#${componentName}__office-location-row-template`),
        distributorSection: this.templates.office.querySelector(`#${componentName}__distributor-section`),
        distributorTableBody: this.templates.office.querySelector(`#${componentName}__office-distributor-table-body`),
        distributorPagination: this.templates.office.querySelector(`#${componentName}__office-distributor-pagination`),
        siSection: this.templates.office.querySelector(`#${componentName}__si-section`),
        siTableBody: this.templates.office.querySelector(`#${componentName}__office-si-table-body`),
        siPagination: this.templates.office.querySelector(`#${componentName}__office-si-pagination`),
    };

    // Product elements
    productEls = {
        facetListsContainer: this.templates.product.querySelector(`.${componentName}__facet-lists-container`),
    };

    // Config
    config = {
        tableRowsPerPage: 10,
    };

    // State
    state = {
        partnerId: null,
        productId: null,
        doc: null,
        map: null,
        infobox: null,
        location: { name: null, latitude: null, longitude: null },
    };

    // ==========
    // BOUND GETTER FUNCTIONS
    // ==========
    /**
     * @param {string} fieldName
     * @param {number} [arrayIndex]
     */
    getField = (fieldName, arrayIndex) => LS.getField(this.state.doc, fieldName, arrayIndex);

    /** @param {string} fieldName */
    getFieldAsArray = (fieldName) => LS.getFieldAsArray(this.state.doc, fieldName);

    /**
     * @param {Record<string, string>} locationFields
     * @param {number} [arrayIndex]
     * @param {boolean} [shortFormat]
     */
    getAddressString = (locationFields, arrayIndex, shortFormat) => LS.getAddressString(this.state.doc, locationFields, arrayIndex, shortFormat);

    /**
     * @param {Record<string, string>} regionFields
     * @param {number} [arrayIndex]
     */
    getRegions = (regionFields, arrayIndex) => LS.getRegions(this.state.doc, regionFields, arrayIndex);

    // ==========
    // LOAD CONTENT
    // ==========
    loadContent = async () => {
        window.parent.dispatchEvent(new CustomEvent('show-loading'));

        // Read user location if no stored location was set
        if (this.state.location.latitude == null || this.state.location.longitude == null) {
            this.state.location = await LS.readUserLocation();
        }

        // Read URL param information
        const url = new URL(window.location.href);
        this.state.partnerId = url.searchParams.get(LS.OtherUrlParams.accountId);
        this.state.productId = url.searchParams.get(LS.OtherUrlParams.productId);
        const isSalesOffice = Boolean(url.searchParams.get(LS.OtherUrlParams.isSalesOffice));
        const aprCode = url.searchParams.get(LS.OtherUrlParams.aprCode);

        // For sales offices, request additional partner location information asynchronously
        let salesOfficePartnerLocationsPromise;
        if (isSalesOffice) {
            salesOfficePartnerLocationsPromise = this.loadSalesOfficePartnerLocations();
        }

        this.state.location = await LS.readStoredLocation();

        let data;
        try {
            data = await LS.fetchData({
                facets: `id:${this.state.partnerId}`,
                startIndex: 0,
                numResults: 1,
            });
        } catch {
            // Error case with no data is handled below
        }
        this.state.doc = data?.response?.docs?.[0];
        if (!this.state.doc) {
            this.showFailedToLoad();
        } else if (this.state.productId) {
            await this.showProduct();
        } else if (isSalesOffice) {
            await this.showSalesOffice();
            await salesOfficePartnerLocationsPromise;
        } else if (aprCode != null) {
            await this.showOperatingCompany(this.state.doc, aprCode);
        } else {
            await this.showPartner();
        }

        window.parent.dispatchEvent(new CustomEvent('hide-loading'));
        this.updatePageMetadata();
    };

    loadSalesOfficePartnerLocations = async () => {
        let data;
        try {
            data = await LS.fetchData({
                facets: `rl_sales_office_branch_ss:${this.state.partnerId}`,
                startIndex: 0,
                numResults: 999,
            });
        } catch {
            // Error case with no data is handled below
        }

        const docs = data?.response?.docs;
        if (!docs) {
            return;
        }

        const distributorRows = docs
            .filter((doc) => LS.getField(doc, LS.Fields.PartnerType) === LS.PartnerTypes.Distributor)
            .flatMap(this.createOfficeLocationRows);
        if (distributorRows.length > 0) {
            this.officeEls.distributorTableBody.append(...distributorRows);
            this.updateTableState(this.officeEls.distributorTableBody, this.officeEls.distributorPagination);
            this.officeEls.distributorSection.hidden = false;
        }

        const systemIntegratorRows = docs
            .filter((doc) => LS.getField(doc, LS.Fields.PartnerType) === LS.PartnerTypes.SI)
            .flatMap(this.createOfficeLocationRows);
        if (systemIntegratorRows.length > 0) {
            this.officeEls.siTableBody.append(...systemIntegratorRows);
            this.updateTableState(this.officeEls.siTableBody, this.officeEls.siPagination);
            this.officeEls.siSection.hidden = false;
        }
    };

    showFailedToLoad = () => {
        console.error('Failed to load partner data'); // TODO: display error message or redirect to 404?
    };

    // ==========
    // POPULATE CONTENT
    // ==========
    /**
     * Show one template and remove the others
     * @param {HTMLElement} template
     */
    showOnlyTemplate = (template) => {
        template.hidden = false;
        Object.values(this.templates).forEach((temp) => {
            if (temp !== template) {
                temp.remove();
            }
        });
    };

    /** Populate and display partner information */
    showPartner = async () => {
        const partnerType = this.getField(LS.Fields.PartnerType);
        const isTechnologyPartner = partnerType === LS.PartnerTypes.TechnologyPartner;

        // ==========
        // PRE-COMPUTE DATA
        // ==========
        // Pre-compute website links
        const websiteLinks = {
            website: this.getField(LS.Fields.Url),
            facebook: this.getField(LS.Fields.FacebookUrl),
            linkedin: this.getField(LS.Fields.LinkedInUrl),
            twitter: this.getField(LS.Fields.TwitterUrl),
            youtube: this.getField(LS.Fields.YouTubeUrl),
        };

        // Pre-compute expertise/offerings facet lists
        const certsAndAffils = this.getFieldAsArray(LS.Fields.Certifications).map((certName, index) => ({
            label: certName,
            link: this.getField(LS.Fields.CertificationsUrl, index),
            type: Number(this.getField(LS.Fields.CertificationsType, index)),
        }));
        const certifications = certsAndAffils.filter(({ type }) => type === LS.CertificationTypes.Certification);
        const affiliations = certsAndAffils.filter(({ type }) => type === LS.CertificationTypes.Affiliation);

        const allCapabilities = this.getFieldAsArray(LS.Fields.Disciplines).map((capaName, index) => ({
            label: capaName,
            group: this.getField(LS.Fields.DisciplinesGroup, index),
        }));
        const projectLifecycleCapabilities = allCapabilities.filter(({ group }) => group === LS.CapabilitiesGroups.ProjectLifecycle);
        const designCapabilities = allCapabilities.filter(({ group }) => group === LS.CapabilitiesGroups.Design);
        const otherCapabilities = allCapabilities.filter(({ group }) => !Object.values(LS.CapabilitiesGroups).includes(group));

        // pre-compute the industry badge based on IndustryBadgeFlags
        const industries = this.getFieldAsArray(LS.Fields.Industries);
        const industryBadgeFlags = this.getFieldAsArray(LS.Fields.IndustryBadgeFlags);
        const showIndustryBadges = industries.filter((industry, index) => industryBadgeFlags[index] === true);

        const facetListsMap = [
            { header: 'Project Lifecycle Capabilities', labels: projectLifecycleCapabilities.map(({ label }) => label) },
            { header: 'Design Capabilities', labels: designCapabilities.map(({ label }) => label) },
            { header: 'Industries', labels: industries },
            { header: 'Capabilities', labels: otherCapabilities.map(({ label }) => label) },
            { header: 'Machine Segments', labels: this.getFieldAsArray(LS.Fields.MachineSegments) },
            { header: 'Machine Sub-Segments', labels: this.getFieldAsArray(LS.Fields.MachineSubSegments) },
            { header: 'Product Categories', labels: [...new Set(this.getFieldAsArray(LS.Fields.ProductCategories))].sort(new Intl.Collator().compare) },
            {
                header: 'Third-Party Certifications',
                labels: certifications.map(({ label }) => label),
                links: certifications.map(({ link }) => link),
            },
            {
                header: 'Affiliations',
                labels: affiliations.map(({ label }) => label),
                links: affiliations.map(({ link }) => link),
            },
            {
                header: [LS.PartnerTypes.OEM, LS.PartnerTypes.SI, LS.PartnerTypes.TechnologyPartner].includes(partnerType)
                    ? 'Partner Highlights'
                    : 'Success Stories',
                labels: this.getFieldAsArray(LS.Fields.SuccessStories).map((label) => label?.replaceAll('_', ' ')),
                links: this.getFieldAsArray(LS.Fields.SuccessStoriesUrl),
            },
        ].filter(({ labels }) => labels.length > 0);

        // Pre-compute expertise badges
        let expertiseBadges = [];
        if (partnerType === LS.PartnerTypes.SI || partnerType === LS.PartnerTypes.OEM) {
            try {
                const expertiseBadgeServletResponse = await fetch('/bin/rockwell-automation/locator-badges');
                const expertiseBadgeJson = await expertiseBadgeServletResponse.json();
                expertiseBadgeJson.forEach((badge) => {
                    const badgeEl = document.createElement('img');
                    badgeEl.src = badge.src;
                    badgeEl.setAttribute('data-badge-name', badge.name);
                    badgeEl.classList.add(this.selectors.expertiseBadge.replace('.', ''));
                    this.partnerEls.expertiseBadgesTemplate.content.appendChild(badgeEl);
                });

                const badgeFacetListsMap = facetListsMap.flatMap((facetList) => (facetList.header === 'Industries' ? showIndustryBadges : facetList.labels)); // replace industry with only flagged badges within SI and OEM
                const badgeFacetNames = badgeFacetListsMap.map((label) => `${partnerType}_${label}`); // This format matches the authoring guidelines for the `partner-locator-badges` generic list
                const badges = this.partnerEls.expertiseBadgesTemplate.content.querySelectorAll(this.selectors.expertiseBadge);
                expertiseBadges = [...badges].filter((badge) => badgeFacetNames.includes(badge.dataset.badgeName)); // Get all badges that match the facet and partner type
                expertiseBadges.forEach((badge) => badge.setAttribute('alt', badge.dataset.badgeName.split('_')[1]));
            } catch (error) {
                console.warn('Failed to load expertise badges:', error);
            }
        }

        // Pre-compute location information
        const primaryLocationId = this.getField(LS.Fields.PrimaryLocationId);
        const locations = this.getFieldAsArray(LS.Fields.Locations.Id).map((locationId, index) => ({
            locationId,
            locationName: this.getField(LS.Fields.Locations.Name, index) ?? this.getField(LS.Fields.Name),
            address: this.getAddressString(LS.Fields.Locations, index),
            email: this.getField(LS.Fields.Locations.Email, index),
            phone: this.getField(LS.Fields.Locations.Phone, index),
            url: this.getField(LS.Fields.Locations.Url, index),
            latitude: this.getField(LS.Fields.Locations.Latitude, index),
            longitude: this.getField(LS.Fields.Locations.Longitude, index),
            regions: this.getRegions(LS.Fields.Locations.Regions, index),
            isHeadquarters: primaryLocationId === locationId,
        })).sort(this.compareGeoDistance);

        // ==========
        // POPULATE CONTENT
        // ==========
        // INFO AND OVERVIEW SECTIONS
        const hasStats = this.getField(LS.Fields.YearEstablished) || this.getField(LS.Fields.TotalEmployees) || this.getField(LS.Fields.TotalEngineers);
        const hasValueToCustomer = this.getField(LS.Fields.TechPartner.ValueToCustomer1) || this.getField(LS.Fields.TechPartner.ValueToCustomer2) || this.getField(LS.Fields.TechPartner.ValueToCustomer3);
        LS.fillTemplate(this.templates.partner, 'data-x', {
            // Info section
            partnerName: this.getField(LS.Fields.Name),
            partnerImgAlt: `${this.getField(LS.Fields.Name)} logo`,
            hqAddress: locations.find((location) => location.isHeadquarters)?.address,
            hasWebsite: Object.values(websiteLinks).some((value) => value),
            ...websiteLinks,
            hasContactInfo: ![LS.PartnerTypes.OEM, LS.PartnerTypes.SI, LS.PartnerTypes.EPCPartner].includes(partnerType) // Hide top contact section for OEM, SI, EPC
                && (this.getField(LS.Fields.Phone) || this.getField(LS.Fields.PrimaryLocation.Email)),
            // Overview section
            hasOverview: this.getField(LS.Fields.Description) || hasStats || this.getField(LS.Fields.Locations.Id),
            companyOverviewTitle: this.getField(LS.Fields.TechPartner.CompanyOverviewTitle),
            description: this.getField(LS.Fields.Description),
            hasStats,
            yearEstablished: this.getField(LS.Fields.YearEstablished),
            totalEmployees: this.getField(LS.Fields.TotalEmployees),
            totalEngineers: this.getField(LS.Fields.TotalEngineers),
            badgeSrc: LS.LogoMap[this.getField(LS.Fields.ProgramType)],
            badgeImgAlt: `${this.getField(LS.Fields.PartnerSubtype) ? `${this.getField(LS.Fields.PartnerSubtype)} ` : ''}${this.getField(LS.Fields.PartnerType)} Rockwell Automation Partner badge`,
            valueStatement: this.getField(LS.Fields.TechPartner.ValuesStatementSection),
            portfolioOffering: this.getField(LS.Fields.TechPartner.PortfolioOffering),
            hasValueToCustomer,
            valueToCustomer1: this.getField(LS.Fields.TechPartner.ValueToCustomer1),
            valueToCustomer2: this.getField(LS.Fields.TechPartner.ValueToCustomer2),
            valueToCustomer3: this.getField(LS.Fields.TechPartner.ValueToCustomer3),
            keyTakeaways1: this.getField(LS.Fields.TechPartner.KeyTakeaways1),
            keyTakeaways2: this.getField(LS.Fields.TechPartner.KeyTakeaways2),
            keyTakeaways3: this.getField(LS.Fields.TechPartner.KeyTakeaways3),
            hasLocations: this.getField(LS.Fields.Locations.Id),
            // Show/hide other sections
            hasExpertise: facetListsMap.length > 0 || this.getField(LS.Fields.Products.Id),
            hasExpertiseBadges: expertiseBadges.length > 0,
            hasFacets: facetListsMap.length > 0,
            hasProducts: this.getField(LS.Fields.Products.Id),
            hasContacts: this.getField(LS.Fields.Contacts.Id) && partnerType !== LS.PartnerTypes.Distributor, // DMX-2417: Sales contacts should not show for distributors
            isTechnologyPartner,
            hasHeadquarters: locations.some((location) => location.isHeadquarters),
            hasOtherLocations: locations.some((location) => !location.isHeadquarters),
        });
        this.fillContactButtonTemplate(this.partnerEls.infoContactButtons, {
            email: this.getField(LS.Fields.PrimaryLocation.Email),
            title: this.getField(LS.Fields.Name),
            phone: this.getField(LS.Fields.Phone),
        });
        // Load partner logo asynchronously
        (async () => {
            const partnerLogo = await LS.getPartnerLogo(this.getField(LS.Fields.PrimaryLocation.Id), this.state.partnerId);
            if (partnerLogo) {
                this.partnerEls.infoImage.src = partnerLogo;
                // Add error handler for 404 or other load failures
                this.partnerEls.infoImage.addEventListener('error', () => {
                    this.partnerEls.infoImage.hidden = true;
                });
            }
        })();
        // Load portal image asynchronously
        if (isTechnologyPartner && this.getField(LS.Fields.TechPartner.ShowImageOnLocator)) {
            (async () => {
                const portalImage = await LS.getPortalImage(this.state.partnerId);
                if (portalImage) {
                    this.partnerEls.portalImage.src = portalImage;
                }
            })();
        }
        // Load operating company logos asynchronously
        if (partnerType === LS.PartnerTypes.Distributor) {
            const aprCodes = new Set(this.getFieldAsArray(LS.Fields.Distributor.AprCode));
            const opCoLogos = new Set();
            [...aprCodes].forEach(async (aprCode) => {
                const logoSrc = await LS.getOpCoLogo({ apiData: this.state.doc, aprCode });
                if (logoSrc && !opCoLogos.has(logoSrc)) {
                    opCoLogos.add(logoSrc);
                    // Create link
                    const opCoLink = this.createOpCoLink(this.state.partnerId, aprCode);
                    const linkEl = this.createImageLink(logoSrc, opCoLink);
                    this.partnerEls.relatedCompaniesContainer.appendChild(linkEl);
                    this.partnerEls.relatedCompanies.hidden = false;
                    this.partnerEls.operatingCompaniesTitle.hidden = false;
                    // Add error handler for 404 or other load failures
                    linkEl.addEventListener('error', () => {
                        linkEl.hidden = true;
                    });
                }
            });
        }

        // EXPERTISE/OFFERINGS SECTION
        // Expertise badges
        this.partnerEls.expertiseBadgesContainer.append(...expertiseBadges);
        // Facet lists
        const facetLists = facetListsMap.map(this.createFacetList);
        this.partnerEls.facetListsContainer.append(...facetLists.filter((list) => list != null));
        // Product table
        const products = this.getFieldAsArray(LS.Fields.Products.Id).map((productId, index) => (
            this.createProductRow({
                productId,
                productName: this.getField(LS.Fields.Products.Name, index),
                productCategory: this.getField(LS.Fields.Products.Category, index),
                productDescription: this.getField(LS.Fields.Products.Description, index) || this.getField(LS.Fields.Products.DescriptionTypo, index), // TEMP: fallback for typo
            })
        ));
        this.partnerEls.productTableBody.append(...products);
        this.updateTableState(this.partnerEls.productTableBody, this.partnerEls.productPagination);

        // REGIONAL/SALES CONTACTS SECTION
        const salesContacts = this.getFieldAsArray(LS.Fields.Contacts.Id).map((contactId, index) => (
            this.createContactRow({
                firstName: this.getField(LS.Fields.Contacts.FirstName, index),
                lastName: this.getField(LS.Fields.Contacts.LastName, index),
                location: isTechnologyPartner
                    ? this.getRegions(LS.Fields.Contacts.Regions, index).join(', ') // Use contact region for Technology Partners
                    : this.getAddressString(LS.Fields.Contacts.Location, index, true),
                email: this.getField(LS.Fields.Contacts.Email, index),
                phone: this.getField(LS.Fields.Contacts.Phone, index),
            })));
        this.partnerEls.contactsTableBody.append(...salesContacts);

        // LOCATIONS SECTION
        this.showOnlyTemplate(this.templates.partner); // The containing section needs to be visible so that Bing maps sizes itself correctly
        if (!isTechnologyPartner) {
            // Display map and location list for partner types other than Technology Partner
            this.initMap(this.partnerEls.locationsMap, locations);
            locations.forEach((location) => {
                // Add location to list
                const listElement = this.createLocationListElement(location);
                if (location.isHeadquarters) {
                    this.partnerEls.locationsHeadquartersContainer.appendChild(listElement);
                } else {
                    this.partnerEls.locationsOtherContainer.appendChild(listElement);
                }
            });
        } else {
            // For technology partner, display locations by region
            locations.forEach((location) => {
                // Add location to all applicable regions
                location.regions.forEach((region) => {
                    const listElement = this.createLocationListElement(location);
                    const regionContainer = this.partnerEls.regionsContentContainer.querySelector(`[data-region="${region}"]`);
                    regionContainer.appendChild(listElement);
                    regionContainer.hidden = false;
                });
            });
        }
    };

    /**
     * Populate and display distributor operating company information
     *
     * Uses some data from parent company, and some data from the locations
     * with matching APR codes.
     * @param {Object} apiData
     * @param {string} aprCode
     */
    showOperatingCompany = async (apiData, aprCode) => {
        const opCoIdx = LS.getNearestOpCoIndex(apiData, aprCode, this.state.location);

        /** @param {string} fieldName */
        const getOpCoField = (fieldName) => this.getField(fieldName, opCoIdx);
        const opCoAprId = getOpCoField(LS.Fields.Distributor.Location.AprId);

        // ==========
        // PRE-COMPUTE DATA
        // ==========
        // Pre-compute website links
        const websiteLinks = {
            website: getOpCoField(LS.Fields.Distributor.AprUrl),
            facebook: getOpCoField(LS.Fields.Distributor.FacebookUrl),
            linkedin: getOpCoField(LS.Fields.Distributor.LinkedInUrl),
            twitter: getOpCoField(LS.Fields.Distributor.TwitterUrl),
            youtube: getOpCoField(LS.Fields.Distributor.YouTubeUrl),
        };

        // Pre-compute expertise/offerings facet lists
        const certsAndAffils = this.getFieldAsArray(LS.Fields.Certifications).map((certName, index) => ({
            label: certName,
            link: this.getField(LS.Fields.CertificationsUrl, index),
            type: Number(this.getField(LS.Fields.CertificationsType, index)),
        }));
        const certifications = certsAndAffils.filter(({ type }) => type === LS.CertificationTypes.Certification);
        const affiliations = certsAndAffils.filter(({ type }) => type === LS.CertificationTypes.Affiliation);

        const lineCardUrl = getOpCoField(LS.Fields.Distributor.LineCardUrl);
        const facetListsMap = [
            {
                header: 'Third-Party Certifications',
                labels: certifications.map(({ label }) => label),
                links: certifications.map(({ link }) => link),
            },
            {
                header: 'Affiliations',
                labels: affiliations.map(({ label }) => label),
                links: affiliations.map(({ link }) => link),
            },
            {
                header: 'Line Card',
                labels: lineCardUrl ? ['Line Card'] : [],
                links: lineCardUrl ? [lineCardUrl] : [],
            }
        ].filter(({ labels }) => labels.length > 0);

        // Pre-compute location information
        const locations = this.getFieldAsArray(LS.Fields.Distributor.LocationId)
            .map((locationId, distributorIdx) => ({
                locationId,
                distributorIdx,
                locationIdx: this.getFieldAsArray(LS.Fields.Locations.Id).indexOf(locationId),
                aprId: this.getField(LS.Fields.Distributor.Location.AprId, distributorIdx),
            }))
            .filter(({ locationIdx, aprId }) => locationIdx !== -1 && aprId === opCoAprId) // Only include locations with matching apr ID
            .map(({ distributorIdx, locationIdx }) => ({
                locationName: this.getField(LS.Fields.Distributor.Location.Name, distributorIdx) ?? this.getField(LS.Fields.Locations.Name, locationIdx),
                address: this.getAddressString(LS.Fields.Locations, locationIdx),
                email: this.getField(LS.Fields.Locations.Email, locationIdx) ?? this.getField(LS.Fields.Distributor.Location.Email, distributorIdx),
                phone: this.getField(LS.Fields.Distributor.LocationPhone, distributorIdx) ?? this.getField(LS.Fields.Locations.Phone, locationIdx),
                url: this.getField(LS.Fields.Distributor.Location.Url) ?? this.getField(LS.Fields.Locations.Url, locationIdx),
                latitude: this.getField(LS.Fields.Locations.Latitude, locationIdx),
                longitude: this.getField(LS.Fields.Locations.Longitude, locationIdx),
            }))
            .sort(this.compareGeoDistance);

        // ==========
        // POPULATE CONTENT
        // ==========
        // INFO AND OVERVIEW SECTIONS
        const hasStats = this.getField(LS.Fields.YearEstablished) || this.getField(LS.Fields.TotalEmployees) || this.getField(LS.Fields.TotalEngineers);
        LS.fillTemplate(this.templates.partner, 'data-x', {
            // Info section
            partnerName: getOpCoField(LS.Fields.Distributor.Name),
            partnerImgAlt: `${getOpCoField(LS.Fields.Distributor.Name)} logo`,
            hasWebsite: Object.values(websiteLinks).some((value) => value),
            ...websiteLinks,
            hasContactInfo: getOpCoField(LS.Fields.Distributor.Email) || getOpCoField(LS.Fields.Distributor.Phone),
            // Overview section
            hasOverview: true, // Parent company is always populated
            description: this.getField(LS.Fields.Description),
            hasStats,
            yearEstablished: this.getField(LS.Fields.YearEstablished),
            totalEmployees: this.getField(LS.Fields.TotalEmployees),
            totalEngineers: this.getField(LS.Fields.TotalEngineers),
            badgeSrc: LS.LogoMap[this.getField(LS.Fields.ProgramType)],
            badgeImgAlt: `${this.getField(LS.Fields.PartnerSubtype) ? `${this.getField(LS.Fields.PartnerSubtype)} ` : ''}${this.getField(LS.Fields.PartnerType)} Rockwell Automation Partner badge`,
            hasLocations: true,
            // Show/hide other sections
            hasExpertise: facetListsMap.length > 0 || this.getField(LS.Fields.Products.Id),
            hasFacets: facetListsMap.length > 0,
            hasProducts: this.getField(LS.Fields.Products.Id),
            hasContacts: false, // Contacts not displayed on distributor pages
            hasHeadquarters: false, // All locations are listed together
            hasOtherLocations: true,
        });
        this.fillContactButtonTemplate(this.partnerEls.infoContactButtons, {
            email: getOpCoField(LS.Fields.Distributor.Email),
            title: getOpCoField(LS.Fields.Distributor.Name),
            phone: getOpCoField(LS.Fields.Distributor.Phone),
        });
        // Load operating company logo asynchronously
        (async () => {
            const opCoLogo = await LS.getOpCoLogo({
                apiData: this.state.doc,
                aprCode,
                parentLocationId: this.getField(LS.Fields.PrimaryLocation.Id),
                parentPartnerId: this.state.partnerId,
            });
            if (opCoLogo) {
                this.partnerEls.infoImage.src = opCoLogo;
                // Add error handler for 404 or other load failures
                this.partnerEls.infoImage.addEventListener('error', () => {
                    this.partnerEls.infoImage.hidden = true;
                });
            }
        })();
        // Load parent logo asynchronously
        (async () => {
            const logoSrc = await LS.getPartnerLogo(this.getField(LS.Fields.PrimaryLocation.Id), this.state.partnerId);
            if (logoSrc) {
                // Create link
                const parentLink = this.createPartnerLink(this.state.partnerId);
                const linkEl = this.createImageLink(logoSrc, parentLink);
                this.partnerEls.relatedCompaniesContainer.appendChild(linkEl);
                this.partnerEls.relatedCompanies.hidden = false;
                this.partnerEls.parentCompanyTitle.hidden = false;
                
                // Add error handler for 404 or other load failures
                linkEl.querySelector("img").addEventListener("error", () => {
                    linkEl.hidden = true;
                    // If .locator-detail__related-companies-container only has a tags that are hidden, hide the container
                    const anchorElements =
                        this.partnerEls.relatedCompaniesContainer?.querySelectorAll(
                            "a",
                        ) || [];
                    let allHidden = true;
                    for (let i = 0; i < anchorElements.length; i++) {
                        if (!anchorElements[i].hidden) {
                            allHidden = false;
                            break;
                        }
                    }
                    if (allHidden) {
                        console.log("Hiding related companies container");
                        this.partnerEls.relatedCompanies.hidden = true;
                    }
                });
            }
        })();

        // EXPERTISE/OFFERINGS SECTION
        // Facet lists
        const facetLists = facetListsMap.map(this.createFacetList);
        this.partnerEls.facetListsContainer.append(...facetLists.filter((list) => list != null));
        // Product table
        const products = this.getFieldAsArray(LS.Fields.Products.Id).map((productId, index) => (
            this.createProductRow({
                productId,
                productName: this.getField(LS.Fields.Products.Name, index),
                productCategory: this.getField(LS.Fields.Products.Category, index),
                productDescription: this.getField(LS.Fields.Products.Description, index) || this.getField(LS.Fields.Products.DescriptionTypo, index), // TEMP: fallback for typo
            })
        ));
        this.partnerEls.productTableBody.append(...products);
        this.updateTableState(this.partnerEls.productTableBody, this.partnerEls.productPagination);

        // LOCATIONS SECTION
        this.showOnlyTemplate(this.templates.partner); // The containing section needs to be visible so that Bing maps sizes itself correctly
        this.initMap(this.partnerEls.locationsMap, locations);
        locations.forEach((location) => {
            // Add location to list
            const listElement = this.createLocationListElement(location);
            this.partnerEls.locationsOtherContainer.appendChild(listElement);
        });
    };

    /** Populate and display sales office information */
    showSalesOffice = async () => {
        // Populate content
        LS.fillTemplate(this.templates.office, 'data-x', {
            officeName: this.getField(LS.Fields.SalesOffice.DisplayName) ?? this.getField(LS.Fields.SalesOffice.Name),
            location: this.getAddressString(LS.Fields.SalesOffice.Location),
            email: this.getField(LS.Fields.SalesOffice.Email),
            mainPhone: this.getField(LS.Fields.SalesOffice.MainPhone),
            servicePhone: this.getField(LS.Fields.SalesOffice.ServicePhone),
            supportPhone: this.getField(LS.Fields.SalesOffice.SupportPhone),
            partsPhone: this.getField(LS.Fields.SalesOffice.PartsPhone),
            fax: this.getField(LS.Fields.SalesOffice.Fax),
            telLink: `tel:${this.getField(LS.Fields.SalesOffice.MainPhone)}`,
        });

        // Initialize map
        this.showOnlyTemplate(this.templates.office); // The containing section needs to be visible so that Bing maps sizes itself correctly
        const location = {
            locationName: this.getField(LS.Fields.SalesOffice.DisplayName) ?? this.getField(LS.Fields.SalesOffice.Name),
            address: this.getAddressString(LS.Fields.SalesOffice.Location),
            email: this.getField(LS.Fields.SalesOffice.Email),
            phone: this.getField(LS.Fields.SalesOffice.MainPhone) ?? this.getField(LS.Fields.SalesOffice.ServicePhone) ?? this.getField(LS.Fields.SalesOffice.SupportPhone) ?? this.getField(LS.Fields.SalesOffice.PartsPhone),
            latitude: this.getField(LS.Fields.SalesOffice.Location.Latitude),
            longitude: this.getField(LS.Fields.SalesOffice.Location.Longitude),
        };
        if (location.latitude != null && location.longitude != null) {
            this.initMap(this.officeEls.map, [location]);
        }
    };

    /** Populate and display product information */
    showProduct = async () => {
        const productIds = this.getFieldAsArray(LS.Fields.Products.Id);
        const productIndex = productIds.findIndex((index) => index === this.state.productId);
        const productData = JSON.parse(this.getFieldAsArray('rl_products')[productIndex]);

        // Pre-compute expertise/offerings facet lists
        const facetListsMap = [
            { header: 'Platforms', labels: productData.platforms?.map((v) => v.Name) },
            { header: 'Industries', labels: productData.productIndustries?.map((v) => v.Name) },
            { header: 'Connectivities', labels: productData.connectivities?.map((v) => v.Name) },
            {
                header: 'Product Links',
                labels: productData.productlinks?.map((v) => v.Description),
                links: productData.productlinks?.map((v) => v.Url),
            },
        ].filter(({ labels }) => labels?.length > 0);

        const images = await Promise.all([
            LS.getProductImage(productData.productPhoto?.productId ?? productData.productPhoto?.[0]?.productId ?? this.state.productId),
            LS.getPartnerLogo(this.getField(LS.Fields.PrimaryLocation.Id), this.state.partnerId),
        ]);

        // Populate content
        LS.fillTemplate(this.templates.product, 'data-x', {
            productImage: images[0],
            productImgAlt: `${productData.ProductName} product image`,
            partnerName: this.getField(LS.Fields.Name),
            partnerLink: this.createPartnerLink(this.getField(LS.Fields.Id)),
            productName: productData.ProductName,
            catalogNumber: productData.CatalogNumber,
            productDescription: productData.ProductDescription ?? productData.ProductDecription, // TEMP: fallback for typo
            productCategory: productData.productcategoryName,
            productOverview: productData.ProductOverview,
            hasFacets: facetListsMap.length > 0,
            partnerLogo: images[1],
            partnerImgAlt: `${this.getField(LS.Fields.Name)} logo`,
        });
        const facetLists = facetListsMap.map(this.createFacetList);
        this.productEls.facetListsContainer.append(...facetLists.filter((list) => list != null));

        this.showOnlyTemplate(this.templates.product);
    };

    // ==========
    // POPULATE CONTENT HELPERS
    // ==========
    /**
     * Populate instances of email/phone buttons
     * @param {Element} parentNode
     * @param {{ email: string, title: string, phone: string }} content
     */
    fillContactButtonTemplate = (parentNode, {
        title, email, phone, url
    }) => {
        LS.fillTemplate(parentNode, 'data-xc', {
            title,
            email,
            phone,
            url: (url && !url.match(/^(\/|http)/)) ? `https://${url}` : url, // If URL is not relative and is missing http prefix, add it
            hasContact: email || phone || url
        });
    };

    /**
     * Create link to partner detail page
     * @param {string} accountId
     * @returns {string}
     */
    createPartnerLink = (accountId, isSalesOffice = false) => {
        const url = new URL(window.location.origin + window.location.pathname);
        url.searchParams.set(LS.OtherUrlParams.accountId, accountId);
        if (isSalesOffice) {
            url.searchParams.set(LS.OtherUrlParams.isSalesOffice, 'true');
        }
        return url.href;
    };

    /**
     * Create link to operating company page
     * @param {string} accountId
     * @param {string} aprCode
     * @returns {string}
     */
    createOpCoLink = (accountId, aprCode) => {
        const url = new URL(window.location.origin + window.location.pathname);
        url.searchParams.set(LS.OtherUrlParams.accountId, accountId);
        url.searchParams.set(LS.OtherUrlParams.aprCode, aprCode);
        return url.href;
    };

    /**
     * Create link to product page
     * @param {string} productId
     * @returns {string}
     */
    createProductLink = (productId) => {
        const url = new URL(window.location.origin + window.location.pathname);
        url.searchParams.set(LS.OtherUrlParams.accountId, this.state.partnerId);
        url.searchParams.set(LS.OtherUrlParams.productId, productId);
        return url.href;
    };

    /**
     * Create image link
     * @param {string} imageSrc
     * @param {string} link
     * @returns {HTMLAnchorElement}
     */
    createImageLink = (imageSrc, link) => {
        const linkEl = document.createElement('a');
        linkEl.href = link;
        const imgEl = document.createElement('img');
        imgEl.src = imageSrc;
        linkEl.appendChild(imgEl);
        return linkEl;
    };

    /**
     * Create a facet list for use in the expertise/offerings section
     * @param {{ header: string, labels: string[], links: string[]? }} params
     * @returns {HTMLElement?}
     */
    createFacetList = ({ header, labels, links = null }) => {
        if (!Array.isArray(labels) || labels.length < 1) {
            return null;
        }

        const clone = this.sharedEls.facetListTemplate.content.cloneNode(true);
        LS.fillTemplate(clone, 'data-xt', { header });
        const listItems = labels.map((label, index) => {
            const listItemEl = document.createElement('li');
            if (links?.[index]) {
                const linkEl = document.createElement('a');
                linkEl.textContent = label;
                linkEl.href = links[index];
                let linkUrl;
                try { // wrap in try/catch in case link is invalid
                    linkUrl = new URL(linkEl.href);
                    if (linkUrl.origin !== window.location.origin) {
                        linkEl.target = '_blank'; // open external links in new tab
                    }
                } catch {
                    // do nothing
                }
                listItemEl.appendChild(linkEl);
            } else {
                listItemEl.textContent = label;
            }
            return listItemEl;
        });

        const listEl = clone.querySelector(`.${componentName}__facet-list`);
        listEl.append(...listItems);
        return clone;
    };

    /**
     * Create product row for use in product table
     * @param {Record<string, string>} params
     * @returns {HTMLTableRowElement}
     */
    createProductRow = ({
        productId, productName, productCategory, productDescription
    }) => {
        const clone = this.partnerEls.productRowTemplate.content.cloneNode(true);
        LS.fillTemplate(clone, 'data-xt', {
            productLink: this.createProductLink(productId),
            productName,
            productCategory,
            productDescription,
        });
        return clone;
    };

    /**
     * Create contact row for use in sales/regional contacts table
     * @param {Record<string, string>} params
     * @returns {HTMLTableRowElement}
     */
    createContactRow = ({
        firstName, lastName, location, email, phone
    }) => {
        const clone = this.partnerEls.contactRowTemplate.content.cloneNode(true);
        const fullName = `${firstName} ${lastName}`;
        LS.fillTemplate(clone, 'data-xt', { contactName: fullName, contactLocation: location }, { keepIfEmpty: true });
        this.fillContactButtonTemplate(clone, { email, title: fullName, phone });
        return clone;
    };

    /**
     * Create office location row for use on Sales Office pages
     * @param {Record<string, string>} params
     * @returns {HTMLTableRowElement}
     */
    createOfficeLocationRow = ({
        company, companyLink, location, email, phone, website
    }) => {
        const clone = this.officeEls.locationRowTemplate.content.cloneNode(true);
        LS.fillTemplate(clone, 'data-xt', {
            company,
            hasCompany: Boolean(company),
            companyLink,
            location,
            hasLocation: Boolean(location),
            website,
            hasWebsite: Boolean(website),
            hasContact: Boolean(email || phone)
        }, { keepIfEmpty: true });
        this.fillContactButtonTemplate(clone, { email, title: company, phone });
        return clone;
    };

    /**
     * Create all office location rows for one partner linked to a Sales Office
     * @param {Object} doc
     * @returns {HTMLTableRowElement[]}
     */
    createOfficeLocationRows = (doc) => LS.getFieldAsArray(doc, LS.Fields.Locations.SalesOfficeBranchId)
        .map((officeBranchId, index) => ({
            officeBranchId,
            company: LS.getField(doc, LS.Fields.Locations.Name, index) ?? LS.getField(doc, LS.Fields.Name),
            companyLink: this.createPartnerLink(LS.getField(doc, LS.Fields.Id)),
            location: LS.getAddressString(doc, LS.Fields.Locations, index, true),
            latitude: LS.getField(doc, LS.Fields.Locations.Latitude, index),
            longitude: LS.getField(doc, LS.Fields.Locations.Longitude, index),
            email: LS.getField(doc, LS.Fields.Locations.Email),
            phone: LS.getField(doc, LS.Fields.Locations.Phone),
            website: LS.getField(doc, LS.Fields.Url),
        }))
        .filter(({ officeBranchId }) => officeBranchId === this.state.partnerId) // Only include locations that are assigned to this sales office
        .sort(this.compareGeoDistance)
        .map((obj) => this.createOfficeLocationRow(obj));

    // ==========
    // LOCATIONS/MAP HELPERS
    // ==========
    /**
     * Initialize Bing map
     * @param {Element} mapElement - The HTML element that the map should be initialized on
     * @param {{ latitude: number, longitude: number }[]} locations - Array of location objects with `{ latitude, longitude }`
     */
    initMap = (mapElement, locations) => {
        const AZURE_MAPS_KEY = LS.getConfig().bingMapsKey;

        const map = new atlas.Map(mapElement, {
            authOptions: {
                authType: 'subscriptionKey',
                subscriptionKey: AZURE_MAPS_KEY,
            },
            center: [0, 0],
            language: 'en-US',
        });

        // Add controls
        map.controls.add(new atlas.control.ZoomControl(), {
            position: 'top-right'
        });

        this.state.map = map;

        // Wait for map resources to be ready
        map.events.add('ready', () => {
            const validLocations = locations
                .map((loc) => ({
                    ...loc,
                    latitude: parseFloat(loc.latitude),
                    longitude: parseFloat(loc.longitude),
                }))
                .filter((loc) => !Number.isNaN(loc.latitude) && !Number.isNaN(loc.longitude));
            const coordinates = validLocations.map((loc) => [loc.longitude, loc.latitude]);

            if (coordinates.length === 1) {
                map.setCamera({
                    center: coordinates[0],
                    zoom: 11
                });
            } else if (coordinates.length > 1) {
                map.setCameraBounds({
                    bounds: atlas.data.BoundingBox.fromPositions(coordinates),
                    padding: 40
                });
            }

            // Add markers
            const datasource = new atlas.source.DataSource();
            map.sources.add(datasource);

            validLocations.forEach((loc) => {
                const listElement = this.createLocationListElement(loc);
                const point = new atlas.data.Feature(
                    new atlas.data.Point([loc.longitude, loc.latitude]),
                    {
                        ...loc,
                        htmlContent: listElement.outerHTML
                    }
                );
                datasource.add(point);
            });

            const symbolLayer = new atlas.layer.SymbolLayer(datasource, null, {
                iconOptions: {
                    image: 'pin-round-red',
                    anchor: 'center',
                    allowOverlap: true
                },
            });
            map.layers.add(symbolLayer);

            map.events.add('mousemove', symbolLayer, () => {
                map.getCanvasContainer().style.cursor = 'pointer';
            });

            map.events.add('mouseout', symbolLayer, () => {
                map.getCanvasContainer().style.cursor = '';
            });

            // Add popup
            const popup = new atlas.Popup();
            map.events.add('click', symbolLayer, (e) => {
                const properties = e.shapes[0].getProperties();
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = properties.htmlContent || '';
                const element = tempDiv;
                const popupContent = this.createPopupContent(element);
                popup.setOptions({
                    position: [properties.longitude, properties.latitude],
                    content: popupContent,
                });
                popup.open(map);
                this.state.map.setCamera({ center: e.position });
            });
            this.state.popup = popup;
        });
    };

    /**
     * Create list element with information for one location
     * @param {Record<string, string>} params
     * @returns {HTMLElement}
     */
    createLocationListElement = ({
        locationName, address, email, phone, url, latitude, longitude
    }) => {
        const clone = this.partnerEls.locationTemplate.content.firstElementChild.cloneNode(true);
        LS.fillTemplate(clone, 'data-xt', { locationName, address });
        this.fillContactButtonTemplate(clone, {
            title: locationName, email, phone, url
        });

        const locationNameEl = clone.querySelector(`.${componentName}__location-name`);
        if (locationNameEl && latitude && longitude) {
            locationNameEl.addEventListener('click', () => {
                const popupContent = this.createPopupContent(clone);

                const position = [parseFloat(longitude), parseFloat(latitude)];
                this.state.popup.setOptions({
                    position,
                    content: popupContent
                });
                this.state.popup.open(this.state.map);
                this.state.map.setCamera({ center: position });
            });
        }

        return clone;
    };

    createPopupContent = (component) => {
        const div = document.createElement('div');
        div.classList.add('map-popup');
        div.classList.add();
        div.style.paddingTop = '10px';
        div.style.paddingBottom = '10px';
        div.style.paddingLeft = '10px';
        div.style.paddingRight = '25px';
        div.style.border = '1px solid #888';
        div.innerHTML = component.innerHTML;
        return div;
    };

    /**
     * Compare geo distance of two locations to `this.state.location`
     * @param {{ latitude: number?, longitude: number? }} location1
     * @param {{ latitude: number?, longitude: number? }} location2
     * @returns {number}
     */
    compareGeoDistance = (location1, location2) => {
        if (this.state.location.latitude != null && this.state.location.longitude != null
            && location1.latitude != null && location1.longitude != null
            && location2.latitude != null && location2.longitude != null) {
            return LS.calcGeoDistance(this.state.location, location1)
                - LS.calcGeoDistance(this.state.location, location2);
        }
        return 0;
    };

    // ==========
    // UI LOGIC
    // ==========
    /**
     * Open/close sections
     * @param {boolean} open
     */
    setSectionsOpenState = (open) => {
        this.sharedEls.sectionContainers.forEach((el) => {
            el.open = open;
        });
    };

    /** Ensure sections remain open on desktop */
    handleMatchMobileChange = () => {
        const isMobile = LS.matchMobile.matches;
        // Expand all sections on desktop
        if (!isMobile) {
            this.setSectionsOpenState(true);
        }
        // Disable pointer and keyboard events on desktop
        this.sharedEls.sectionHeaders.forEach((el) => {
            el.tabIndex = isMobile ? 0 : -1;
            el.style.pointerEvents = isMobile ? '' : 'none';
        });
    };

    /** Update page metadata for SEO purposes */
    updatePageMetadata = () => {
        const url = new URL(window.location.href);
        this.state.partnerId = url.searchParams.get(LS.OtherUrlParams.accountId);
        this.state.productId = url.searchParams.get(LS.OtherUrlParams.productId);
        const isSalesOffice = Boolean(url.searchParams.get(LS.OtherUrlParams.isSalesOffice));

        const ptnrName = this.getField(LS.Fields.Name);
        const ptnrType = this.getField(LS.Fields.PartnerType);
        const ptnrLevel = this.getField(LS.Fields.PartnerSubtype);
        let metaTitle = '';
        let metaDesc = '';

        if (this.state.productId) {
            const productIds = this.getFieldAsArray(LS.Fields.Products.Id);
            const productIndex = productIds.findIndex((index) => index === this.state.productId);
            const prodName = this.getField(LS.Fields.Products.Name, productIndex);
            metaTitle = `${prodName} | Rockwell Automation Partner`;
            metaDesc = `Get details about ${prodName}, a product from ${ptnrName}, a Rockwell Automation ${ptnrLevel ? `${ptnrLevel} ` : ''}${ptnrType === LS.PartnerTypes.TechnologyPartner ? 'Technology' : ptnrType} partner.`;
        } else if (isSalesOffice) {
            metaTitle = `${this.getField(LS.Fields.SalesOffice.DisplayName)} | Rockwell Automation`;
            metaDesc = 'Rockwell Automation Sales Offices provide guidance on improving the productivity and quality of your manufacturing processes.';
        } else if (ptnrName) {
            metaTitle = `${ptnrName} | Rockwell Automation Partner`;
            switch (ptnrType) {
            case LS.PartnerTypes.Distributor:
                metaDesc = `${ptnrName}, a Rockwell Automation Authorized Distributor partner, offers services and support for your automation investment.`;
                break;
            case LS.PartnerTypes.OEM:
                metaDesc = `${ptnrName}, a Rockwell Automation ${ptnrLevel ? `${ptnrLevel} ` : ''}OEM partner, designs, develops, and delivers innovative equipment.`;
                break;
            case LS.PartnerTypes.SI:
                metaDesc = `${ptnrName}, a Rockwell Automation ${ptnrLevel ? `${ptnrLevel} ` : ''}System Integrator partner, designs, builds, installs, delivers, and maintains automation solutions.`;
                break;
            case LS.PartnerTypes.TechnologyPartner:
                metaDesc = `${ptnrName}, a Rockwell Automation Technology Partner, provides complementary products and solutions.`;
                break;
            case LS.PartnerTypes.EPCPartner:
                metaDesc = `${ptnrName}, a Rockwell Automation EPC Partner, offers expertise in professional services spanning design, processes, management, and operations.`;
                break;
            default:
                break;
            }
        }

        if (metaTitle) {
            document.title = metaTitle;
            document.querySelector('meta[property="og:title"]').setAttribute('content', metaTitle);
        }

        const metaTag = document.createElement('meta');
        metaTag.setAttribute('name', 'description');
        metaTag.content = metaDesc;
        document.querySelector('head').appendChild(metaTag);

        const canonical = document.querySelector('link[rel="canonical"]').getAttribute('href');
        if (canonical) {
            document.querySelector('link[rel="canonical"]').setAttribute('href', `${canonical}${url.search}`);
            document.querySelector('meta[property="og:url"]').setAttribute('content', `${canonical}${url.search}`);
        }
    };

    // ==========
    // Hide/show table rows and update pagination
    // ==========
    /**
     * @param {Element} tableBodyEl
     * @param {Element} paginationEl
     */
    updateTableState = (tableBodyEl, paginationEl) => {
        const tableRows = tableBodyEl.querySelectorAll('tr');

        // Validate startIndex
        if (tableBodyEl.startIndex == null || tableBodyEl.startIndex >= tableRows.length) {
            tableBodyEl.startIndex = 0;
        }

        const pageCount = Math.ceil(tableRows.length / this.config.tableRowsPerPage);
        const currentPageNum = Math.floor(tableBodyEl.startIndex / this.config.tableRowsPerPage + 1);

        LS.updatePagination(paginationEl, { currentPageNum, pageCount });

        // Show/hide appropriate rows
        tableRows.forEach((row, index) => {
            row.hidden = ((index < tableBodyEl.startIndex)
                || (index >= tableBodyEl.startIndex + this.config.tableRowsPerPage));
        });
    };

    /**
     * @param {Element} tableBodyEl
     * @param {Element} paginationEl
     */
    handleTablePagination = (tableBodyEl, paginationEl) => {
        if (tableBodyEl.startIndex == null) {
            tableBodyEl.startIndex = 0;
        }
        return LS.handlePagination({
            handlePrevButton: () => tableBodyEl.startIndex -= this.config.tableRowsPerPage,
            handleNextButton: () => tableBodyEl.startIndex += this.config.tableRowsPerPage,
            handleInput: (input) => tableBodyEl.startIndex = (input - 1) * this.config.tableRowsPerPage,
            then: () => this.updateTableState(tableBodyEl, paginationEl),
        });
    };

    /** @param {MouseEvent} e */
    navigateBack = (e) => {
        const filteredPageUrl = sessionStorage.getItem(LS.backLinkSessionStorageKey);
        if (filteredPageUrl) {
            e.preventDefault();
            window.location.href = filteredPageUrl.toString();
        }
    };

    // ==========
    // ADD EVENT LISTENERS
    // ==========
    addEventListeners = () => {
        // Ensure sections remain expanded on desktop
        LS.matchMobile.addEventListener('change', this.handleMatchMobileChange);

        // Header back link button
        this.sharedEls.headerBackLink.addEventListener('click', this.navigateBack);

        // Pagination for product table
        this.partnerEls.productPagination.addEventListener(
            'submit',
            this.handleTablePagination(this.partnerEls.productTableBody, this.partnerEls.productPagination)
        );

        // Sales office contact buttons
        this.officeEls.phoneButton.addEventListener('click', () => {
            this.officeEls.contactDialog.show();
            this.officeEls.phoneButton.dispatchEvent(new CustomEvent(
                'locator__phone-clicked',
                { detail: this.officeEls.phoneButton.dataset, bubbles: true }
            ));
        });

        // Pagination for sales office tables
        this.officeEls.distributorPagination.addEventListener(
            'submit',
            this.handleTablePagination(this.officeEls.distributorTableBody, this.officeEls.distributorPagination)
        );
        this.officeEls.siPagination.addEventListener(
            'submit',
            this.handleTablePagination(this.officeEls.siTableBody, this.officeEls.siPagination)
        );
    };

    // ==========
    // INITIALIZE COMPONENT
    // ==========
    init = () => {
        // Attach contact button methods to window for use with onclick
        // Need to be called from onclick due to contact buttons within the Bing maps infobox
        window.openContactDialog = LS.openContactDialog;
        window.openGatedForm = LS.openGatedForm;

        this.addEventListeners();
        this.handleMatchMobileChange();

        window.addEventListener('load', () => { // Wait for Adobe launch to run
            this.loadContent();
        });

        window.locatorDetailInstance = this; // for debug purposes
    };
}

registerComponent(`.${componentName}`, LocatorDetail);
